import axios from 'axios'

import { TaskType } from '@/app/constants/taskType'
import { useTaskTrackerStore } from '@/modules/shared/stores/taskTrackerStore'

export type StartTrackedTaskResult = {
  taskId: string
  /** True when the server handed us a task that was already running. */
  adopted: boolean
  /** Login of whoever started it, when the server knows — for the message. */
  startedBy?: string | null
}

type Options = {
  companyId: string
  type: TaskType
  /**
   * Task id this client generated. Optional: some flows let the server mint the id
   * and return it, in which case `start` resolves with it.
   */
  taskId?: string
  /**
   * Work scope, mirroring the server's key. Recorded locally so the button for this
   * scope can disable itself immediately, without waiting for the next sync poll.
   */
  scopeKey?: string | null
  /** Local payload for completion handling — downloads, batches, dialogs. */
  data?: unknown
  progressTotal?: number
  /**
   * Issues the request that creates the task server-side. May resolve with the id
   * when the server mints it (`string`, or an object carrying `taskId`).
   */
  start: () => Promise<unknown>
}

/**
 * Starts a long-running task and registers it for tracking, treating "already
 * running" as a normal outcome rather than an error.
 *
 * The server refuses a second active task for the same work scope and answers 409
 * with the id of the one already running. That happens whenever two people in a
 * company click "Przelicz" for the same month, or when someone refreshes the browser
 * and clicks again — previously both produced a duplicate calculation, because the
 * task list lived only in the tab's memory. Here we adopt the running task instead:
 * the user ends up watching the work they asked for, which is what they wanted.
 */
export const startTrackedTask = async ({
  companyId,
  type,
  taskId,
  scopeKey,
  data,
  progressTotal,
  start,
}: Options): Promise<StartTrackedTaskResult> => {
  const { track, adopt } = useTaskTrackerStore.getState()

  try {
    const started = await start()
    const id = readTaskId(started) ?? taskId
    if (!id) {
      throw new Error('Task started but no id was returned')
    }
    track({ id, companyId, type, scopeKey, data, progressTotal })
    return { taskId: id, adopted: false }
  } catch (error) {
    const conflict = readConflict(error)
    if (!conflict) throw error

    // Pusty GUID = serwer nie potrafil wskazac biegnacego zadania (blokujacy wpis
    // zniknal miedzy naruszeniem indeksu a odczytem). Przejecie go skonczyloby sie
    // odpytywaniem nieistniejacego ID i falszywym bledem u uzytkownika - lepiej
    // przepuscic blad dalej, zeby mogl sprobowac ponownie.
    const existingId =
      conflict.existingTaskId && conflict.existingTaskId !== EMPTY_GUID
        ? conflict.existingTaskId
        : taskId
    if (!existingId || existingId === EMPTY_GUID) throw error

    adopt(existingId, companyId, type, data)
    return { taskId: existingId, adopted: true, startedBy: conflict.startedBy }
  }
}

const readTaskId = (started: unknown): string | undefined => {
  if (typeof started === 'string') return started || undefined
  if (started && typeof started === 'object') {
    const id = (started as any).taskId ?? (started as any).id
    return typeof id === 'string' && id ? id : undefined
  }
  return undefined
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const readConflict = (
  error: unknown,
): { existingTaskId: string; startedBy?: string | null } | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null
  const body: any = error.response?.data ?? {}
  if (body.code && body.code !== 'TASK_ALREADY_RUNNING') return null
  return {
    existingTaskId: body.existingTaskId ?? body.ExistingTaskId ?? '',
    startedBy: body.startedBy ?? null,
  }
}

/**
 * True when the failure was OUR task quota, not any 429.
 *
 * The edge flood guard also answers 429, and it is keyed by source IP — a NAT'd
 * office loading a screen can trip it on requests that have nothing to do with tasks.
 * Matching on the status alone told those users "too many tasks in a short time",
 * which is both wrong and unactionable, so we require the code the application sends.
 */
export const isTaskRateLimited = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return false
  const code = (error.response?.data as any)?.code
  return code === 'TASK_RATE_LIMITED' || code === 'COMPANY_TASK_LIMIT' || code === 'USER_TASK_RATE_LIMIT'
}
